/**
 * The "jump to section" bar under a page hero: one bold link per section, each pointing at
 * `#<id>` on the same page.
 *
 * Every caller renders it on wide screens only, inside a `Media` breakpoint. The callers are
 * `../BasePage/BasePage.tsx`, the drilled bar, sawing, tube laser, burning and laser cutting
 * pages under `pages/metal-solutions/capabilities/`, the Ryerson solutions pages under
 * `pages/metal-solutions/solutions/`, the Metal Solutions and Metal Resources landing components
 * (`components/MetalSolutions/MetalSolutionsLandingRyerson.tsx`,
 * `components/MetalResources/MetalResourcesLandingRyerson.tsx`, `MetalResourcesLandingSTS.tsx`)
 * and the Contentful product pages (`components/Contentful/Product.tsx`, `SubProduct.tsx`).
 * `pages/about-us/history/index.tsx` defines its own, separate `SubNavigation`.
 *
 * Each id must equal the anchor `components/Shared/AllSections/AllSections.tsx` gives the target
 * section (its title lower-cased, each whitespace run replaced by `-`); callers build both from
 * the same copy. The links are plain anchors (`next={false}`), so a click is the browser's own
 * jump to the element with that id.
 */
import React from "react";
import { Link } from "@ryerson/frontend.navigation";
import styled from "@emotion/styled";
import { useTheme } from "@ryerson/frontend.theme";
import { ContentSection } from "@ryerson/frontend.layout";

/** One link in the bar. */
export type NavigableItem = {
	/** Target element id, without `#`. */
	id: string;
	/** Link text. An empty or missing title renders a link with no text. */
	title: string;
};

/** Props of the default export. */
export interface SubNavigation {
	navigableItems: NavigableItem[];
	/** Theme section type, for the background and link style. Default `"secondary"`. */
	type?: "primary" | "secondary" | "tertiary";
}

const LinkContainer = styled.div`
	text-align: left;
	min-height: 58px;
	height: auto;
	line-height: 58px;
	display: block;
	width: 100%;
`;

const LinkSpan = styled.span`
	display: inline-block;
	vertical-align: middle;
	margin-right: 20px;
	margin-left: 20px;
	&:last-of-type {
		margin-right: 0px;
	}
`;

/** The section link bar; see the file header. */
const SubNavigationComponent: React.FC<SubNavigation> = ({
	navigableItems,
	type = "secondary",
}) => {
	const { theme } = useTheme();

	return (
		<ContentSection vPadding="0" hPadding="0" type={type}>
			<LinkContainer>
				{navigableItems.map((item: NavigableItem, index: number) => {
					return (
						<LinkSpan key={index}>
							<Link
								type={type}
								weight="bold"
								color={theme.colors.primary.secondaryBrand}
								to={"#" + item.id}
								next={false}
							>
								{item.title}
							</Link>
						</LinkSpan>
					);
				})}
			</LinkContainer>
		</ContentSection>
	);
};

export default SubNavigationComponent;
